import React, { useCallback, useRef } from 'react';

import { ImmersiveModalWrapper } from 'src/components/dialogs/ImmersiveModalWrapper';
import { DialogCloseReason } from 'src/components/dialogs/types';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { ActionButtonModal } from 'src/legacy/components/Modals';
import { ResetESignToInitialValues } from 'src/store/signaturePage/actions';
import { setFormPageChanged } from 'src/store/ui/actions';

import {
  ContractBuilderModalContent,
  CreatedContractData,
} from './ContractBuilderModalContent';

export type { CreatedContractData } from './ContractBuilderModalContent';

interface ContractBuilderModalProps {
  isOpen: boolean;
  onClose: (createdContract?: CreatedContractData) => void;
  initialTitle?: string;
  defaultOneOff?: boolean;
  initialRecipient?: { clientId: string; companyId: string } | null;
}

export const ContractBuilderModal = ({
  isOpen,
  onClose,
  initialTitle,
  defaultOneOff,
  initialRecipient,
}: ContractBuilderModalProps) => {
  const dispatch = useAppDispatch();
  const isFormPageChanged = useAppSelector(
    (state) => state.ui.isFormPageChanged,
  );
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);

  // Use refs to avoid recreating callbacks when isFormPageChanged changes,
  // which would cascade re-renders down to FormPage/Formik on every keystroke.
  const isFormPageChangedRef = useRef(isFormPageChanged);
  isFormPageChangedRef.current = isFormPageChanged;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const handleClose = useCallback(
    (_e: unknown, _reason: DialogCloseReason) => {
      if (isFormPageChangedRef.current) {
        setShowConfirmDialog(true);
        return;
      }
      dispatch(setFormPageChanged(false));
      dispatch(ResetESignToInitialValues());
      onCloseRef.current();
    },
    [dispatch],
  );

  const handleConfirmLeave = useCallback(() => {
    dispatch(setFormPageChanged(false));
    dispatch(ResetESignToInitialValues());
    setShowConfirmDialog(false);
    onCloseRef.current();
  }, [dispatch]);

  const handleCancelLeave = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  const handleCloseFromContent = useCallback(
    (createdContract?: CreatedContractData) => {
      if (!createdContract && isFormPageChangedRef.current) {
        setShowConfirmDialog(true);
        return;
      }
      if (createdContract) {
        dispatch(setFormPageChanged(false));
        dispatch(ResetESignToInitialValues());
      }
      onCloseRef.current(createdContract);
    },
    [dispatch],
  );

  if (!isOpen) return null;

  return (
    <>
      <ImmersiveModalWrapper isOpen={isOpen} onClose={handleClose}>
        <ContractBuilderModalContent
          onClose={handleCloseFromContent}
          initialTitle={initialTitle}
          defaultOneOff={defaultOneOff}
          initialRecipient={initialRecipient}
        />
      </ImmersiveModalWrapper>
      <ActionButtonModal
        open={showConfirmDialog}
        onClose={handleCancelLeave}
        title="Unsaved changes"
        mainComponent="If you leave this page, your changes will be lost."
        cancelActionText="Cancel"
        positiveActionText="Leave page"
        positiveAction
        onPositiveActionPerformed={handleConfirmLeave}
      />
    </>
  );
};
